import { SnapshotAssembler, installVisibilityReporter } from './snapshotReceiver.js';
import type { SnapshotDelta } from '../shared/snapshotDelta.js';
import { io } from 'socket.io-client';
import { HybridSocket } from './leader/HybridSocket.js';
import type { LeaderFrameApi } from './leader/LeaderBridge.js';
import type { SocketLike } from './leader/socketLike.js';
import { installHostStatusOverlay } from './hostStatusOverlay.js';
import { postCastLog } from './castDiagnostics.js';
import { EVENTS } from '../shared/events.js';
import type {
  CollisionImpactPayload,
  ControlInput,
  EngineerActionPayload,
  FacilityActionPayload,
  GameSnapshot,
  HostEventLogEntryPayload,
  JoinRoomPayload,
  KickPlayerPayload,
  MarkerSoundTriggeredPayload,
  PowerFailurePayload,
  Role,
  RoleAssignedPayload,
  RoomState,
  SonarPulsePayload,
  StorageActionPayload
} from '../shared/types.js';
import { clearStoredSessionToken, loadStoredSession, state as controllerState } from './controllerState.js';
import { normalizeRenderTuning, type RenderTuning } from './rendering/renderTuning.js';

export type ControllerNetworkCallbacks = {
  setStatus: (message: string) => void;
  showQuickToast: (message: string) => void;
  onConnect: () => void;
  onDisconnected: (reason: string) => void;
  onRoomState: (roomState: RoomState) => Promise<void>;
  onRoleAssigned: (payload: RoleAssignedPayload, wasAutoJoin: boolean) => void;
  onStateSnapshot: (snapshot: GameSnapshot) => void;
  onRenderTuningUpdated: (tuning: RenderTuning) => void;
  onSonarPulse: (payload: SonarPulsePayload) => void;
  onCollisionImpact: (payload: CollisionImpactPayload) => void;
  /** Estimated ms between a world event and its picture showing on the TV (phone-renders casts only). */
  onCastLatency?: (ms: number) => void;
  onPowerFailure: (payload: PowerFailurePayload) => void;
  onMarkerSoundTriggered: (payload: MarkerSoundTriggeredPayload) => void;
  onHostEventLogEntry: (payload: HostEventLogEntryPayload) => void;
  /** Story narration (EVENTS.voiceLine / EVENTS.logUnlocked). Optional so other pages can reuse the callbacks type. */
  onVoiceLine?: (payload: import('../shared/narrative.js').VoiceLinePayload) => void;
  onLogUnlocked?: (payload: import('../shared/narrative.js').LogUnlockedPayload) => void;
  onErrorMessage: (payload: { message: string }) => void;
  onNodeRevealRestored: (payload: { nodeRevealState: string }) => void;
  onRoomClosed: () => void;
};

export type ControllerNetwork = {
  joinRoom: (code: string, name: string) => void;
  chooseRole: (role: Role) => void;
  engineerAction: (payload: EngineerActionPayload) => void;
  storageAction: (payload: StorageActionPayload) => void;
  facilityAction: (payload: FacilityActionPayload) => void;
  emitControl: (input: ControlInput) => void;
  kickPlayer: (payload: KickPlayerPayload) => void;
  attemptAutoJoin: () => void;
  restartGame: (code: string) => void;
  startGame: (code: string) => void;
  startNow: (code: string) => void;
  cancelStart: (code: string) => void;
  /** This player leaves the current room; the crew keeps playing. */
  leaveRoom: () => void;
  /** End the mission for everyone: shows the summary, then closes the room. */
  endGame: (code: string) => void;
  setNavWaypoint: (waypoint: { simulationX: number; simulationZ: number; depth?: number } | null) => void;
  setCastQuality: (quality: 'low' | 'medium' | 'high') => void;
  /** Set the TV's music / effects volumes (0..1). */
  setCastAudio: (music: number, effects: number) => void;
  /** Generic passthrough for shared modules (castSetup) that emit their own events. */
  emitEvent: (event: string, payload: unknown) => void;
  /** Returns the underlying Socket.IO socket, e.g. for the phone renderer to share. */
  getSocket: () => SocketLike;
};

const realSocket = io({ transports: ['websocket'] });

/**
 * Inside the host page's hidden controller frame while the host device leads the room, game events go straight to the in-device
 * worker (no trip through the server); anywhere else this is the plain socket.
 */
const parentLeader = (): LeaderFrameApi | null => {
  try {
    if (window.parent === window) return null;
    const api = (window.parent as unknown as { __aegisLeader?: LeaderFrameApi }).__aegisLeader;
    return api?.active ? api : null;
  } catch { return null; }
};
const leaderApi = parentLeader();
const socket: SocketLike = leaderApi ? new HybridSocket(realSocket, leaderApi, false) : realSocket;

// Last resource-node list received from the server. The tick snapshot omits it
// unless it changed, so we cache and refill (see the stateSnapshot handler).
let cachedResourceNodes: GameSnapshot['world']['resourceNodes'] = [];

export const createControllerNetwork = (callbacks: ControllerNetworkCallbacks): ControllerNetwork => {
  const attemptAutoJoin = (): void => {
    const storedSession = loadStoredSession();
    if (!storedSession) {
      callbacks.setStatus('Connected. Enter a room code to join.');
      return;
    }

    controllerState.autoJoinPending = true;
    const payload: JoinRoomPayload = {
      code: storedSession.code,
      name: storedSession.name,
      reconnectToken: storedSession.reconnectToken
    };

    socket.emit(EVENTS.joinRoom, payload);
    callbacks.setStatus(`Rejoining ${storedSession.code}...`);
  };

  // While the host device is away the room is paused: say so, and hold inputs back.
  const hostStatus = installHostStatusOverlay(socket, () => controllerState.currentRoomCode ?? null);

  socket.on('connect', () => {
    callbacks.onConnect();
  });

  // A new device took over hosting the room (restored from its checkpoint): every player is "disconnected" there until they rejoin.
  socket.on(EVENTS.hostChanged, () => {
    if (controllerState.currentRoomCode) attemptAutoJoin();
  });

  // The server going away mid-mission (process stopped, crash, or the phone
  // losing its connection) leaves no state update arriving, so the controller
  // would otherwise freeze on a stale mission screen. Surface it so the UI can
  // message the player and return them to setup. Socket.IO still retries in the
  // background; a genuine reconnect re-runs onConnect afterwards.
  socket.on('disconnect', (reason: string) => {
    // 'io client disconnect' only happens when we tear down the socket
    // ourselves — never a server outage — so ignore it.
    if (reason === 'io client disconnect') {
      return;
    }
    callbacks.onDisconnected(reason);
  });

  socket.on(EVENTS.roomState, (roomState: RoomState) => {
    void callbacks.onRoomState(roomState);
  });

  socket.on(EVENTS.customMapReloaded, () => {
    if (controllerState.lastRoomState) {
      void callbacks.onRoomState(controllerState.lastRoomState);
    }
  });

  socket.on(EVENTS.roleAssigned, (payload: RoleAssignedPayload) => {
    const wasAutoJoin = controllerState.autoJoinPending;
    controllerState.currentPlayerId = payload.playerId;
    controllerState.currentRole = payload.role;
    controllerState.reconnectToken = payload.reconnectToken ?? controllerState.reconnectToken;
    controllerState.autoJoinPending = false;
    callbacks.onRoleAssigned(payload, wasAutoJoin);
  });

  // The server omits world.resourceNodes on ticks where the list is unchanged
  // (bandwidth optimisation). Cache the last received list and refill it so
  // every downstream consumer still sees a fully-populated snapshot.
  const snapshotAssembler = new SnapshotAssembler(() => {
    if (controllerState.currentRoomCode) socket.emit(EVENTS.requestFullSnapshot, { code: controllerState.currentRoomCode });
  });
  // Stall telemetry (see cast.ts): snapshots come every 100 ms; a longer silence on a visible page is a visible pause.
  let lastSnapshotPerfMs = 0;
  let lastStallLoggedAtMs = 0;
  const noteSnapshotGap = (tick: number): void => {
    const now = performance.now();
    const gap = lastSnapshotPerfMs > 0 ? now - lastSnapshotPerfMs : 0;
    lastSnapshotPerfMs = now;
    if (gap > 250 && document.visibilityState === 'visible' && now - lastStallLoggedAtMs > 1000) {
      lastStallLoggedAtMs = now;
      postCastLog('controller: snapshot gap', { gapMs: Math.round(gap), tick, route: leaderApi?.active ? 'local' : 'server', socketConnected: realSocket.connected, inFrame: window.parent !== window });
    }
  };
  const handleSnapshot = (snapshot: GameSnapshot): void => {
    noteSnapshotGap(snapshot.tick);
    if (snapshot.world.resourceNodes) {
      cachedResourceNodes = snapshot.world.resourceNodes;
    } else {
      snapshot.world.resourceNodes = cachedResourceNodes;
    }
    callbacks.onStateSnapshot(snapshot);
  };
  socket.on(EVENTS.stateSnapshot, (snapshot: GameSnapshot) => handleSnapshot(snapshotAssembler.full(snapshot)));
  socket.on(EVENTS.stateSnapshotDelta, (delta: SnapshotDelta) => {
    const rebuilt = snapshotAssembler.delta(delta);
    if (rebuilt) handleSnapshot(rebuilt);
  });
  socket.on('connect', () => snapshotAssembler.reset());
  installVisibilityReporter(socket, EVENTS.clientVisibility);

  socket.on(EVENTS.renderTuningUpdated, (payload: unknown) => {
    const tuning = normalizeRenderTuning(payload as Partial<RenderTuning>);
    if (typeof callbacks.onRenderTuningUpdated === 'function') {
      callbacks.onRenderTuningUpdated(tuning);
    }
  });

  socket.on(EVENTS.sonarPulse, (payload: SonarPulsePayload) => {
    callbacks.onSonarPulse(payload);
  });

  socket.on(EVENTS.collisionImpact, (payload: CollisionImpactPayload) => {
    callbacks.onCollisionImpact(payload);
  });

  socket.on(EVENTS.castLatency, (payload: { ms?: number }) => {
    if (typeof payload?.ms === 'number') callbacks.onCastLatency?.(payload.ms);
  });

  socket.on(EVENTS.powerFailure, (payload: PowerFailurePayload) => {
    callbacks.onPowerFailure(payload);
  });

  socket.on(EVENTS.markerSoundTriggered, (payload: MarkerSoundTriggeredPayload) => {
    callbacks.onMarkerSoundTriggered(payload);
  });

  socket.on(EVENTS.voiceLine, (payload: import('../shared/narrative.js').VoiceLinePayload) => callbacks.onVoiceLine?.(payload));
  socket.on(EVENTS.logUnlocked, (payload: import('../shared/narrative.js').LogUnlockedPayload) => callbacks.onLogUnlocked?.(payload));

  socket.on(EVENTS.hostEventLogEntry, (payload: HostEventLogEntryPayload) => {
    callbacks.onHostEventLogEntry(payload);
  });

  socket.on(EVENTS.errorMessage, (payload: { message: string }) => {
    if (controllerState.autoJoinPending) {
      clearStoredSessionToken();
    }
    callbacks.onErrorMessage(payload);
  });

  socket.on(EVENTS.nodeRevealRestored, (payload: { nodeRevealState: string }) => {
    callbacks.onNodeRevealRestored(payload);
  });

  socket.on(EVENTS.roomClosed, () => {
    callbacks.onRoomClosed();
  });

  const joinRoom = (code: string, name: string): void => {
    controllerState.autoJoinPending = true;
    controllerState.currentPhase = 'lobby';
    socket.emit(EVENTS.joinRoom, {
      code,
      name,
      reconnectToken: controllerState.reconnectToken
    });
    callbacks.setStatus(`Joining ${code}...`);
  };

  const chooseRole = (role: Role): void => {
    if (!controllerState.currentRoomCode) {
      return;
    }

    socket.emit(EVENTS.chooseRole, {
      code: controllerState.currentRoomCode,
      role
    });
  };

  const engineerAction = (payload: EngineerActionPayload): void => {
    if (hostStatus.isPaused() || !controllerState.currentRoomCode) {
      return;
    }
    socket.emit(EVENTS.engineerAction, payload);
  };

  const storageAction = (payload: StorageActionPayload): void => {
    if (hostStatus.isPaused()) return;
    socket.emit(EVENTS.storageAction, payload);
  };

  const facilityAction = (payload: FacilityActionPayload): void => {
    if (hostStatus.isPaused()) return;
    socket.emit(EVENTS.facilityAction, payload);
  };

  const kickPlayer = (payload: KickPlayerPayload): void => {
    socket.emit(EVENTS.kickPlayer, payload);
  };

  const emitControl = (input: ControlInput): void => {
    if (hostStatus.isPaused()) return; // the room is paused until its host returns
    if (!controllerState.currentRoomCode || !controllerState.currentRole) {
      return;
    }

    socket.emit(EVENTS.controlInput, {
      code: controllerState.currentRoomCode,
      input
    });
  };

  const restartGame = (code: string): void => {
    socket.emit(EVENTS.restartGame, { code });
  };

  const startNow = (code: string): void => {
    socket.emit(EVENTS.startNow, { code });
  };

  const cancelStart = (code: string): void => {
    socket.emit(EVENTS.cancelStart, { code });
  };

  const startGame = (code: string): void => {
    socket.emit(EVENTS.startGame, { code });
  };

  const leaveRoom = (): void => {
    const code = controllerState.currentRoomCode;
    if (!code) return;
    socket.emit(EVENTS.leaveRoom, { code });
  };

  const endGame = (code: string): void => {
    socket.emit(EVENTS.endGame, { code });
  };

  const setNavWaypoint = (waypoint: { simulationX: number; simulationZ: number; depth?: number } | null): void => {
    if (!controllerState.currentRoomCode) return;
    socket.emit(EVENTS.setNavWaypoint, { code: controllerState.currentRoomCode, waypoint });
  };

  const setCastQuality = (quality: 'low' | 'medium' | 'high'): void => {
    if (!controllerState.currentRoomCode) return;
    socket.emit(EVENTS.setCastQuality, { code: controllerState.currentRoomCode, quality });
  };

  const setCastAudio = (music: number, effects: number): void => {
    if (!controllerState.currentRoomCode) return;
    socket.emit(EVENTS.setCastAudio, { code: controllerState.currentRoomCode, music, effects });
  };

  const emitEvent = (event: string, payload: unknown): void => {
    socket.emit(event, payload);
  };

  return {
    joinRoom,
    chooseRole,
    engineerAction,
    storageAction,
    facilityAction,
    emitControl,
    kickPlayer,
    attemptAutoJoin,
    restartGame,
    startGame,
    startNow,
    cancelStart,
    leaveRoom,
    endGame,
    setNavWaypoint,
    setCastQuality,
    setCastAudio,
    emitEvent,
    getSocket: () => socket,
  };
};
